import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, patchRound, deleteClaim } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Shuffle, Lock, LockOpen, DotsSixVertical, DotsThreeVertical, Trophy, ArrowsClockwise, WarningCircle, ArrowCounterClockwise } from '@phosphor-icons/react';
import { roundHasScores } from '../lib/formatHelpers';
import { setRoundUnlockToken } from '../lib/ownerAuth';
import { getTripAccess } from '../lib/tripAccess';
import OwnerPasswordDialog from '../components/OwnerPasswordDialog';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  generateRoundDraw, playingHandicap, isShotgunRound, isSplitTeeRound, startHoleForGroup, teeTimeForGroup,
  computeFinalRoundSeededDraw, formatHandicapIndex, formatPlayingHandicap, getEffectivePH, getAllowedPH, displayWHS,
} from '../golf';
import { effectiveCourseForPlayer } from '../lib/effectiveCourse';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { sortedGroupIndexes, groupsSharingHoleMap, parseGroupNameKey, holeLabelForGroup, groupDisplayName } from '../lib/groupOrder';
import { balancedScrambleDraw } from '../lib/scramble';

function PlayerRow({ player, ph, groupIdx, groupNames, totalGroups, onMoveToGroup, locked, round }) {
  if (!player) return null;
  return (
    <div className="flex items-center px-4 min-h-[52px] bg-white" data-testid={`team-player-${player.id}`}>
      <div className="w-8 h-8 rounded-full bg-[#E8E5DB] flex items-center justify-center text-[#1B4332] font-bold text-xs mr-3 flex-shrink-0">
        {player.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
      </div>
      <span className="flex-1 text-sm font-medium text-[#1D242B] truncate">{player.name}</span>
      <Badge className="bg-[#F4F6F5] text-[#455561] text-xs font-semibold mr-1">WHS {displayWHS(player)}</Badge>
      <Badge className="bg-[#1B4332] text-white text-xs font-bold mr-1">PH {formatPlayingHandicap(ph)}</Badge>
      {!locked && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1.5 text-[#B8C7C0] hover:text-[#1B4332] transition-colors rounded-md hover:bg-[#F4F6F5]" data-testid={`move-player-${player.id}`}>
              <DotsThreeVertical className="w-5 h-5" weight="bold" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[160px]">
            {Array.from({ length: totalGroups }, (_, i) => i).filter(i => i !== groupIdx).map(i => (
              <DropdownMenuItem key={i} onClick={() => onMoveToGroup(player.id, groupIdx, i)} data-testid={`move-to-group-${i}`}>
                Move to {groupDisplayName(round, i)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function SortableGroup({ id, children, disabled }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : 'auto', opacity: isDragging ? 0.85 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`sortable-group-${id}`}>
      {children({ dragHandleProps: { ...attributes, ...listeners }, isDragging })}
    </div>
  );
}

export default function TeamDraw() {
  const { code, roundId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamSize, setTeamSize] = useState(4);
  const [groupNames, setGroupNames] = useState([]);
  const [ownerUnlocked, setOwnerUnlocked] = useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const etagRef = useRef(null);

  // Access & role — computed early (reads localStorage, no round dependency)
  const access = getTripAccess(code);
  const role = access?.role || (access?.legacy ? 'organiser' : null);
  const isOrganiser = role === 'organiser';

  const groupSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const load = useCallback(async () => {
    try {
      const res = await getTrip(code, etagRef.current);
      if (res.status === 304) return; // unchanged
      etagRef.current = (res.headers?.etag || '').replace('W/', '') || null;
      const tripData = res.data;
      setTrip(tripData);
      const r = tripData.rounds?.find(r => r.id === roundId);
      if (r) {
        setRound(r);
        setTeamSize(r.team_size || 4);
        setTeams(r.teams || []);
        setGroupNames(r.group_names || []);

        // Auto-generate final round draw when all conditions are met
        if (r.is_final && isOrganiser && !r.final_draw_generated_at && (!r.teams || r.teams.length === 0)) {
          const fmt = r.format || 'stableford';
          const priorRevealed = (tripData.rounds || []).filter(pr =>
            pr.id !== roundId && (pr.format || 'stableford') === fmt && pr.revealed === true
          );
          const pending = (tripData.rounds || []).filter(pr =>
            pr.id !== roundId && (pr.format || 'stableford') === fmt && pr.revealed !== true
          );
          if (priorRevealed.length > 0 && pending.length === 0) {
            setAutoGenerating(true);
            // Inherit team_size from most-recent prior matching-format round
            const mostRecent = priorRevealed[priorRevealed.length - 1];
            const inheritedTs = mostRecent.team_size || 4;
            const newTeams = computeFinalRoundSeededDraw(tripData, r, priorRevealed, inheritedTs);
            if (newTeams.length > 0) {
              const names = newTeams.map((_, i) => `Group ${i + 1}`);
              try {
                const pRes = await patchRound(code, roundId, {
                  teams: newTeams, group_names: names,
                  team_size: inheritedTs,
                  final_draw_generated_at: new Date().toISOString(),
                });
                setTrip(pRes.data);
                setTeamSize(inheritedTs);
                const pr = pRes.data.rounds?.find(pr2 => pr2.id === roundId);
                if (pr) { setRound(pr); setTeams(pr.teams || []); setGroupNames(pr.group_names || []); }
              } catch (e) { console.error('Auto-generate seeded draw failed', e); }
            }
            setAutoGenerating(false);
          }
        }
      }
    } catch {} finally { setLoading(false); }
  }, [code, roundId, isOrganiser]);
  useEffect(() => { load(); }, [load]);

  // ── Final Round computed values ──
  const isFinalRound = round?.is_final === true;
  const roundFormat = round?.format || 'stableford';
  const pendingRounds = isFinalRound ? (trip?.rounds || []).filter(r =>
    r.id !== roundId && (r.format || 'stableford') === roundFormat && r.revealed !== true
  ) : [];
  const priorFormatRounds = isFinalRound ? (trip?.rounds || []).filter(r =>
    r.id !== roundId && (r.format || 'stableford') === roundFormat && r.revealed === true
  ) : [];
  const hasPriorFormatRounds = priorFormatRounds.length > 0 || pendingRounds.length > 0;
  const finalWaiting = isFinalRound && pendingRounds.length > 0;
  const finalNoData = isFinalRound && !hasPriorFormatRounds;
  const drawAlreadyGenerated = !!round?.final_draw_generated_at;

  // Inherited team_size for final rounds: use the most-recent prior matching-format round's team_size.
  const inheritedTeamSize = (() => {
    if (!isFinalRound || priorFormatRounds.length === 0) return null;
    // priorFormatRounds are in trip.rounds order — last element is most recent
    const mostRecent = priorFormatRounds[priorFormatRounds.length - 1];
    const ts = mostRecent.team_size || 4;
    // Warn if prior rounds disagree
    const sizes = new Set(priorFormatRounds.map(r => r.team_size || 4));
    if (sizes.size > 1) console.warn(`Final Round: prior format-matching rounds have inconsistent team_size values (${[...sizes].join(', ')}). Using most-recent: ${ts}.`);
    return ts;
  })();

  // Poll when in final-round waiting state (15s ±2s jitter)
  useEffect(() => {
    if (!finalWaiting) return;
    let timer;
    const tick = () => {
      const jitter = 13000 + Math.random() * 4000;
      timer = setTimeout(() => { load(); tick(); }, jitter);
    };
    tick();
    return () => clearTimeout(timer);
  }, [finalWaiting, load]);

  const players = trip?.players || [];
  const playerMap = Object.fromEntries(players.map(p => [p.id, p]));

  const getPH = (player) => {
    if (!player || !round?.course) return 0;
    return getAllowedPH(player, effectiveCourseForPlayer(round, player.id), round);
  };

  // ── Randomise: non-final rounds only ──
  const handleRandomize = async () => {
    if (typeof navigator.vibrate === 'function') navigator.vibrate([10]);
    const pids = players.filter(p => p.active !== false).map(p => p.id);
    const isScramble = round?.scoring_mode === 'texas_scramble';

    let newTeams;
    if (isScramble && trip && round) {
      // Balanced scramble draw: minimise max-min team PH spread
      newTeams = balancedScrambleDraw(round, trip, pids, teamSize);
    } else {
      // Individual: existing random draw
      const orderFn = (id) => {
        const p = playerMap[id];
        if (!p) return 99;
        return p.handicap_mode === 'ph' && p.manual_ph != null ? p.manual_ph : p.whs_index;
      };
      const prevTeams = (trip?.rounds || [])
        .filter(r => r.id !== roundId && r.teams?.length)
        .map(r => r.teams);
      newTeams = generateRoundDraw(pids, orderFn, teamSize, prevTeams, 400);
    }

    const sorted = (!isShotgunRound(round) && !isSplitTeeRound(round))
      ? [...newTeams].sort((a, b) => a.length - b.length)
      : newTeams;
    const names = sorted.map((_, i) => groupNames[i] || `Group ${i + 1}`);
    // Persist to server immediately (matching handleRegenerate pattern)
    try {
      const res = await patchRound(code, roundId, {
        teams: sorted, group_names: names, team_size: teamSize, teams_locked: false,
      });
      setTrip(res.data);
      const r = res.data.rounds?.find(r => r.id === roundId);
      if (r) { setRound(r); setTeams(r.teams || []); setGroupNames(r.group_names || []); }
    } catch {
      // Fallback: keep client state even if save fails
      setTeams(sorted);
      setGroupNames(names);
    }
  };

  // ── Final Round: generate / regenerate seeded draw ──
  const handleRegenerate = async () => {
    if (!trip || !round) return;
    const ts = inheritedTeamSize || teamSize;
    setAutoGenerating(true);
    try {
      const newTeams = computeFinalRoundSeededDraw(trip, round, priorFormatRounds, ts);
      if (newTeams.length === 0) return;
      const names = newTeams.map((_, i) => `Group ${i + 1}`);
      const res = await patchRound(code, roundId, {
        teams: newTeams, group_names: names,
        team_size: ts,
        final_draw_generated_at: new Date().toISOString(),
        teams_locked: false,
      });
      setTrip(res.data);
      setTeamSize(ts);
      const r = res.data.rounds?.find(r => r.id === roundId);
      if (r) { setRound(r); setTeams(r.teams || []); setGroupNames(r.group_names || []); }
    } catch (e) { console.error('Regenerate seeded draw failed', e); }
    finally { setAutoGenerating(false); }
  };

  const handleSave = async (lock = false) => {
    setSaving(true);
    const startHoles = isShotgunRound(round)
      ? teams.map((_, i) => startHoleForGroup(round, i))
      : undefined;
    try {
      const res = await patchRound(code, roundId, {
        teams, team_size: teamSize, group_names: groupNames, teams_locked: lock,
        ...(startHoles ? { group_start_holes: startHoles } : {}),
      });
      setTrip(res.data);
      const r = res.data.rounds?.find(r => r.id === roundId);
      if (r) { setRound(r); setTeams(r.teams || []); }
      if (lock) navigate(`/trip/${code}/round/${roundId}/setup`);
    } catch {} finally { setSaving(false); }
  };

  const handleUnlock = async () => {
    setSaving(true);
    try {
      const res = await patchRound(code, roundId, { teams_locked: false });
      setTrip(res.data);
      const r = res.data.rounds?.find(r => r.id === roundId);
      if (r) setRound(r);
    } catch {} finally { setSaving(false); }
  };

  const handleResetDraw = async () => {
    setResetting(true);
    try {
      await api.post(`/trips/${code}/rounds/${roundId}/reset-draw`);
      setResetModalOpen(false);
      toast.success('Draw reset. You can now redo the draw.');
      load();
    } catch (e) {
      setResetModalOpen(false);
      if (e.response?.status === 409) {
        toast.error("Draw can't be reset once scoring has started");
        load();
      } else {
        toast.error('Failed to reset draw. Please try again.');
      }
    } finally { setResetting(false); }
  };


  // Drag-and-drop: move player between/within teams
  // ── Group-level reorder ──
  const groupIds = teams.map((_, i) => `group-${i}`);
  // Merge local state (teams/groupNames) into round for display-order and badge computations,
  // so that unsaved changes (randomise, drag) are immediately reflected.
  const localRound = round ? { ...round, teams, group_names: groupNames } : null;
  const displayOrder = localRound ? sortedGroupIndexes(localRound) : teams.map((_, i) => i);
  const holeCounts = localRound ? groupsSharingHoleMap(localRound) : new Map();
  const handleGroupDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = groupIds.indexOf(active.id);
    const newIdx = groupIds.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    setTeams(prev => arrayMove(prev, oldIdx, newIdx));
    setGroupNames(prev => arrayMove(prev, oldIdx, newIdx));
  };

  const handleStartHoleChange = (teamIdx, hole) => {
    const newHole = parseInt(hole);
    const startHoles = teams.map((_, i) => {
      if (i === teamIdx) return newHole;
      return round?.group_start_holes?.[i] || startHoleForGroup(round, i);
    });

    // Auto-suggest letter suffix when sharing a hole with a default-named group
    const sharedCount = startHoles.filter((h, i) => i !== teamIdx && h === newHole).length;
    let updatedNames = null;

    if (sharedCount >= 1) {
      const currentName = groupNames[teamIdx] || '';
      const isDefault = /^Group\s+\d+$/.test(currentName) || currentName === '';
      if (isDefault) {
        const usedSuffixes = new Set();
        teams.forEach((_, i) => {
          if (i === teamIdx) return;
          if (startHoles[i] === newHole) {
            const parsed = parseGroupNameKey(groupNames[i] || round?.group_names?.[i] || '');
            if (parsed.num === newHole) usedSuffixes.add(parsed.suffix);
          }
        });
        let nextSuffix = 'A';
        while (usedSuffixes.has(nextSuffix)) nextSuffix = String.fromCharCode(nextSuffix.charCodeAt(0) + 1);
        const newName = `${newHole}${nextSuffix}`;
        updatedNames = [...groupNames];
        updatedNames[teamIdx] = newName;
        setGroupNames(updatedNames);
        toast.info(`Named this group "${newName}" — sharing hole ${newHole}`);
      }
    }

    const patchBody = { group_start_holes: startHoles };
    if (updatedNames) patchBody.group_names = updatedNames;

    patchRound(code, roundId, patchBody).then(res => {
      setTrip(res.data);
      const r = res.data.rounds?.find(r => r.id === roundId);
      if (r) { setRound(r); if (!updatedNames) setGroupNames(r.group_names || []); }
    }).catch(() => {});
  };

  // ── Tap-based player move (cross-group) ──
  const handleMoveToGroup = (playerId, fromGroupIdx, toGroupIdx) => {
    setTeams(prev => {
      const next = prev.map(t => [...t]);
      next[fromGroupIdx] = next[fromGroupIdx].filter(id => id !== playerId);
      next[toGroupIdx] = [...next[toGroupIdx], playerId];
      return next;
    });
  };

  if (loading) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;
  if (!round) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><p className="text-[#455561] text-sm">Round not found.</p></div>;

  const shotgun = isShotgunRound(round);
  const hasScores = round ? roundHasScores(round) : false;
  const scoredLocked = hasScores && !ownerUnlocked;
  const editLocked = isOrganiser ? scoredLocked : true;

  return (
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="team-draw">
      <div className="bg-[#1D242B] px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(`/trip/${code}/round/${roundId}/setup`)} className="text-white p-1" data-testid="back-btn"><ArrowLeft className="w-5 h-5" weight="bold" /></button>
        <div className="flex-1"><h1 className="text-xl text-white font-bold">Team Draw</h1><p className="text-xs text-[#B8C7C0]">{round?.label} · {round?.course?.name}</p></div>
        {isFinalRound && <Badge className="bg-[#C9A227] text-white text-xs font-semibold" data-testid="final-round-header-badge">Final</Badge>}
        {round?.teams_locked && <Badge className="bg-[#1B4332] text-white text-xs font-semibold">Locked</Badge>}
      </div>

      <div className="px-4 py-6 max-w-lg mx-auto space-y-4 animate-fade-in-up">
        {/* Owner lock banner — organiser only, only when round has scores */}
        {isOrganiser && hasScores && (
          <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold ${
            scoredLocked ? 'bg-[#FFF3E0] text-[#E59000] border border-[#FFE0B2]' : 'bg-[#E8F5E9] text-[#2E7D32] border border-[#C8E6C9]'
          }`} data-testid="team-draw-lock-banner">
            {scoredLocked ? (
              <>
                <Lock className="w-4 h-4 flex-shrink-0" weight="bold" />
                <span className="flex-1">Master password required — round has scores</span>
                <button onClick={() => setOwnerDialogOpen(true)}
                  className="ml-auto px-3 py-1 text-xs font-bold bg-[#E59000] text-white rounded-lg hover:bg-[#D48400] transition-colors whitespace-nowrap"
                  data-testid="unlock-teamdraw-btn">Unlock (owner)</button>
              </>
            ) : (
              <><LockOpen className="w-4 h-4 flex-shrink-0" weight="bold" /><span>Unlocked — team changes allowed</span></>
            )}
          </div>
        )}
        <OwnerPasswordDialog open={ownerDialogOpen} onOpenChange={setOwnerDialogOpen}
          onSuccess={(token) => { setOwnerDialogOpen(false); setRoundUnlockToken(code, roundId, token); setOwnerUnlocked(true); }}
          description="This round has scores recorded. Enter the master password to unlock team changes."
          tripCode={code} />

        {/* Final Round: seeded-by-standings badge */}
        {isFinalRound && drawAlreadyGenerated && teams.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[#1B4332]/5 border border-[#1B4332]/20" data-testid="final-seeded-badge">
            <Trophy className="w-4 h-4 text-[#1B4332]" weight="fill" />
            <span className="text-sm font-semibold text-[#1B4332]">Final Round · Seeded by standings</span>
          </div>
        )}

        {/* Team size — organiser only; read-only hint for final rounds */}
        {isOrganiser && (
          isFinalRound ? (
            inheritedTeamSize && (
            <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl" data-testid="team-size-inherited">
              <label className="text-sm font-semibold text-[#455561] mb-2 block">Players per group</label>
              <div className="flex gap-2">
                {[2, 3, 4].map(s => (
                  <Button key={s} variant={inheritedTeamSize === s ? 'default' : 'outline'} disabled
                    className={`flex-1 h-12 rounded-xl font-bold ${inheritedTeamSize === s ? 'bg-[#1B4332] text-white' : 'border-[#E1E6E4]'} opacity-60`}
                    data-testid={`team-size-${s}`}>{s}-Ball</Button>
                ))}
              </div>
              <p className="text-xs text-[#B8C7C0] mt-2">Inherited from prior rounds — cannot be changed for a Final Round.</p>
            </Card>
            )
          ) : (
          <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl">
            <label className="text-sm font-semibold text-[#455561] mb-2 block">Players per group</label>
            <div className="flex gap-2">
              {[2, 3, 4].map(s => (
                <Button key={s} variant={teamSize === s ? 'default' : 'outline'}
                  disabled={editLocked}
                  className={`flex-1 h-12 rounded-xl font-bold ${teamSize === s ? 'bg-[#1B4332] text-white' : 'border-[#E1E6E4]'} ${editLocked ? 'opacity-40' : ''}`}
                  onClick={() => !editLocked && setTeamSize(s)} data-testid={`team-size-${s}`}>{s}-Ball</Button>
              ))}
            </div>
          </Card>
          )
        )}

        {/* ── Teams display area ── */}
        {isFinalRound && finalWaiting ? (
          /* Final Round: Waiting for prior rounds to be revealed */
          <Card className="p-6 bg-white border-[#E1E6E4] rounded-xl text-center space-y-3" data-testid="final-waiting-card">
            <WarningCircle className="w-10 h-10 text-[#E59000] mx-auto" weight="fill" />
            <p className="font-semibold text-[#1D242B]">Waiting for prior rounds to be revealed</p>
            <p className="text-sm text-[#455561]">
              The final round draw will be seeded by standings once all prior{' '}
              {roundFormat === 'nsp' ? 'Net Stroke Play' : 'Stableford'} rounds are revealed.
            </p>
            <div className="space-y-1 mt-2">
              {pendingRounds.map(r => (
                <div key={r.id} className="text-xs text-[#E59000] font-semibold bg-[#FFF3E0] rounded-lg px-3 py-1.5">
                  {r.label} — not yet revealed
                </div>
              ))}
            </div>
          </Card>
        ) : isFinalRound && finalNoData ? (
          /* Final Round: No prior format-matching rounds exist */
          <Card className="p-6 bg-white border-[#E1E6E4] rounded-xl text-center space-y-3" data-testid="final-nodata-card">
            <WarningCircle className="w-10 h-10 text-[#B8C7C0] mx-auto" weight="fill" />
            <p className="font-semibold text-[#1D242B]">No prior rounds to seed from</p>
            <p className="text-sm text-[#455561]">
              Add and reveal at least one {roundFormat === 'nsp' ? 'Net Stroke Play' : 'Stableford'} round
              before this one, or turn off Final Round in Round Settings.
            </p>
          </Card>
        ) : autoGenerating ? (
          /* Auto-generating spinner */
          <Card className="p-6 bg-white border-[#E1E6E4] rounded-xl text-center" data-testid="final-generating-card">
            <div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm font-semibold text-[#455561]">Generating standings-based draw...</p>
          </Card>
        ) : teams.length === 0 ? (
          <Card className="p-8 bg-white border-[#E1E6E4] rounded-xl text-center">
            {isOrganiser ? (
              isFinalRound ? (
                <>
                  <p className="text-[#455561] text-sm">All prior rounds are revealed. Tap below to generate the standings-seeded draw.</p>
                  <p className="text-xs text-[#B8C7C0] mt-1">{players.filter(p => p.active !== false).length} players available</p>
                </>
              ) : (
                <>
                  <p className="text-[#455561] text-sm">Tap Randomize to generate balanced teams</p>
                  <p className="text-xs text-[#B8C7C0] mt-1">{players.filter(p => p.active !== false).length} players available</p>
                </>
              )
            ) : (
              <p className="text-[#455561] text-sm">No groups assigned yet. Your organiser will set up the draw.</p>
            )}
          </Card>
        ) : isOrganiser ? (
          /* Organiser: single DndContext for both group reorder + player cross-group drag */
          <DndContext sensors={groupSensors} collisionDetection={closestCenter}
            onDragEnd={!editLocked && !round?.teams_locked ? handleGroupDragEnd : undefined}
            modifiers={[restrictToVerticalAxis]}>
            <SortableContext items={displayOrder.map(i => `group-${i}`)} strategy={verticalListSortingStrategy}>
              <div className="space-y-4">
                {displayOrder.map(teamIdx => {
                  const team = teams[teamIdx];
                  const sh = startHoleForGroup(round, teamIdx);
                  const isShared = (holeCounts.get(sh) || 0) >= 2;
                  return (
                  <SortableGroup key={`group-${teamIdx}`} id={`group-${teamIdx}`} disabled={editLocked || !!round?.teams_locked}>
                    {({ dragHandleProps, isDragging }) => (
                      <Card className={`bg-white border-[#E1E6E4] rounded-xl overflow-hidden ${isDragging ? 'ring-2 ring-[#1B4332] shadow-lg' : ''}`} data-testid={`team-card-${teamIdx}`}>
                        <div className="px-4 py-2.5 bg-[#F4F6F5] border-b border-[#E1E6E4] flex items-center gap-2">
                          {!editLocked && !round?.teams_locked && (
                            <button className="p-1 -ml-1 text-[#B8C7C0] hover:text-[#1B4332] cursor-grab active:cursor-grabbing touch-none transition-colors" {...dragHandleProps} data-testid={`group-drag-handle-${teamIdx}`}>
                              <DotsSixVertical className="w-5 h-5" weight="bold" />
                            </button>
                          )}
                          <Input value={groupNames[teamIdx] || groupDisplayName(localRound, teamIdx)}
                            onChange={(e) => { if (!editLocked) { const n = [...groupNames]; n[teamIdx] = e.target.value; setGroupNames(n); } }}
                            disabled={editLocked}
                            className="h-8 text-sm font-bold bg-transparent border-0 p-0 focus-visible:ring-0 flex-1 disabled:opacity-100" data-testid={`group-name-${teamIdx}`} />
                          {isShared && <Badge className="bg-slate-100 text-slate-600 text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap" data-testid={`shared-badge-${teamIdx}`}>Shared · hole {sh}</Badge>}
                          <Badge className="bg-[#E8E5DB] text-[#1B4332] text-[10px] font-semibold px-1.5 whitespace-nowrap" data-testid={`team-hc-sum-${teamIdx}`}>
                            Σ HC {team.reduce((sum, pid) => sum + (playerMap[pid] ? getPH(playerMap[pid]) : 0), 0)}
                          </Badge>
                          {(() => {
                            const tt = teeTimeForGroup(round, teamIdx);
                            return tt ? <Badge className="bg-[#1D242B] text-white text-[10px] font-mono px-1.5" data-testid={`tee-time-${teamIdx}`}>{tt}</Badge> : null;
                          })()}
                          {shotgun && !editLocked && (
                            <div className="flex items-center gap-1">
                              <span className="text-[10px] text-[#455561] whitespace-nowrap">Start:</span>
                              <Select value={holeLabelForGroup(round, teamIdx)}
                                onValueChange={(v) => handleStartHoleChange(teamIdx, v.replace(/^H/, '').replace(/[A-Za-z]+$/, ''))}>
                                <SelectTrigger className="h-7 w-[72px] text-xs rounded-md border-[#E1E6E4]" data-testid={`shotgun-hole-${teamIdx}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {(() => {
                                    const opts = [];
                                    for (let h = 1; h <= 18; h++) {
                                      opts.push(<SelectItem key={`H${h}`} value={`H${h}`}>H{h}</SelectItem>);
                                      let otherCount = 0;
                                      for (let j = 0; j < teams.length; j++) {
                                        if (j === teamIdx) continue;
                                        if (startHoleForGroup(round, j) === h) otherCount++;
                                      }
                                      for (let k = 1; k <= otherCount; k++) {
                                        const letter = String.fromCharCode(64 + k);
                                        opts.push(<SelectItem key={`H${h}${letter}`} value={`H${h}${letter}`}>H{h}{letter}</SelectItem>);
                                      }
                                    }
                                    return opts;
                                  })()}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          {shotgun && editLocked && (() => {
                            const sh2 = startHoleForGroup(round, teamIdx);
                            return sh2 > 1 ? <Badge className="bg-[#E59000] text-white text-[10px] px-1.5">Hole {sh2}</Badge> : null;
                          })()}
                          {/* Release scoring button — organiser/owner only */}
                          {isOrganiser && (() => {
                            const claims = round?.group_claims || [];
                            const now = new Date();
                            const activeClaim = claims.find(c => c.group_idx === teamIdx && new Date(c.expires_at) > now);
                            if (!activeClaim) return null;
                            return (
                              <button
                                className="text-[10px] text-[#D32F2F] font-semibold px-2 py-0.5 rounded border border-[#D32F2F]/30 hover:bg-[#D32F2F]/10 transition-colors whitespace-nowrap"
                                data-testid={`release-claim-${teamIdx}`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await deleteClaim(code, roundId, teamIdx);
                                    await load();
                                    toast.success(`Released scoring for ${groupDisplayName(localRound, teamIdx)}`);
                                  } catch (err) {
                                    toast.error('Failed to release scoring');
                                  }
                                }}>
                                Release scoring
                              </button>
                            );
                          })()}
                        </div>
                        <div className="divide-y divide-[#E1E6E4]">
                          {team.map(pid => {
                            const p = playerMap[pid];
                            return <PlayerRow key={pid} player={p} ph={p ? getPH(p) : 0} groupIdx={teamIdx} groupNames={groupNames} totalGroups={teams.length} onMoveToGroup={handleMoveToGroup} locked={editLocked || !!round?.teams_locked} round={localRound} />;
                          })}
                        </div>
                      </Card>
                    )}
                  </SortableGroup>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          /* Non-organiser: plain read-only view */
          <div className="space-y-4">
            {displayOrder.map(teamIdx => {
              const team = teams[teamIdx];
              const sh = startHoleForGroup(round, teamIdx);
              const isShared = (holeCounts.get(sh) || 0) >= 2;
              return (
              <Card key={teamIdx} className="bg-white border-[#E1E6E4] rounded-xl overflow-hidden" data-testid={`team-card-${teamIdx}`}>
                <div className="px-4 py-2.5 bg-[#F4F6F5] border-b border-[#E1E6E4] flex items-center gap-2">
                  <span className="text-sm font-bold text-[#1D242B] flex-1">{groupNames[teamIdx] || round?.group_names?.[teamIdx] || groupDisplayName(round, teamIdx)}</span>
                  {isShared && <Badge className="bg-slate-100 text-slate-600 text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap" data-testid={`shared-badge-${teamIdx}`}>Shared · hole {sh}</Badge>}
                  <Badge className="bg-[#E8E5DB] text-[#1B4332] text-[10px] font-semibold px-1.5 whitespace-nowrap">
                    Σ HC {team.reduce((sum, pid) => sum + (playerMap[pid] ? getPH(playerMap[pid]) : 0), 0)}
                  </Badge>
                  {(() => {
                    const tt = teeTimeForGroup(round, teamIdx);
                    return tt ? <Badge className="bg-[#1D242B] text-white text-[10px] font-mono px-1.5" data-testid={`tee-time-${teamIdx}`}>{tt}</Badge> : null;
                  })()}
                  {(() => {
                    const sh2 = startHoleForGroup(round, teamIdx);
                    return sh2 > 1 ? <Badge className="bg-[#E59000] text-white text-[10px] px-1.5">Hole {sh2}</Badge> : null;
                  })()}
                </div>
                <div className="divide-y divide-[#E1E6E4]">
                  {team.map(pid => {
                    const p = playerMap[pid];
                    if (!p) return null;
                    return (
                      <div key={pid} className="flex items-center gap-3 px-4 py-3">
                        <span className="text-sm font-medium text-[#1D242B] flex-1 truncate">{p.name}</span>
                        <span className="text-xs text-[#455561]">WHS {displayWHS(p)}</span>
                        <Badge className="bg-[#1B4332] text-white text-[10px] font-bold px-1.5">PH {formatPlayingHandicap(getPH(p))}</Badge>
                      </div>
                    );
                  })}
                </div>
              </Card>
              );
            })}
          </div>
        )}

        {/* Actions — organiser only */}
        {isOrganiser && !finalWaiting && !finalNoData && !autoGenerating && (
        <div className="sticky bottom-0 pt-4 pb-6 space-y-3 bg-[#F4F6F5]">
          {isFinalRound ? (
            /* Final Round: Regenerate + Lock/Unlock */
            <>
              <Button onClick={handleRegenerate} disabled={editLocked || autoGenerating}
                className="w-full h-14 bg-[#1D242B] hover:bg-[#2C3742] text-white rounded-xl text-base font-semibold disabled:opacity-40" data-testid="regenerate-seeded-btn">
                <ArrowsClockwise className={`w-5 h-5 mr-2 ${autoGenerating ? 'animate-spin' : ''}`} weight="bold" />
                {teams.length === 0 ? 'Generate Seeded Draw' : 'Regenerate Seeded Draw'}
              </Button>
              {!round?.teams_locked && (
                <Button onClick={() => handleSave(true)} disabled={saving || editLocked}
                  className="w-full h-14 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl text-base font-semibold disabled:opacity-40" data-testid="lock-draw-btn">
                  <Lock className="w-5 h-5 mr-2" weight="bold" /> {saving ? 'Saving...' : 'Lock Draw'}
                </Button>
              )}
              {round?.teams_locked && (
                <Button onClick={handleUnlock} disabled={saving || editLocked} variant="outline"
                  className="w-full h-14 rounded-xl border-[#E1E6E4] text-[#1D242B] text-base font-semibold disabled:opacity-40" data-testid="unlock-draw-btn">
                  <LockOpen className="w-5 h-5 mr-2" weight="bold" /> Unlock Draw
                </Button>
              )}
            </>
          ) : (
            /* Non-final: existing Randomise + Lock/Unlock pattern */
            !round?.teams_locked ? (
              <>
                <Button onClick={handleRandomize} disabled={editLocked}
                  className="w-full h-14 bg-[#1D242B] hover:bg-[#2C3742] text-white rounded-xl text-base font-semibold disabled:opacity-40" data-testid="randomize-btn">
                  <Shuffle className="w-5 h-5 mr-2" weight="bold" /> Randomize
                </Button>
                <Button onClick={() => handleSave(true)} disabled={saving || editLocked}
                  className="w-full h-14 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl text-base font-semibold disabled:opacity-40" data-testid="lock-draw-btn">
                  <Lock className="w-5 h-5 mr-2" weight="bold" /> {saving ? 'Saving...' : 'Lock Draw'}
                </Button>
              </>
            ) : (
              <Button onClick={handleUnlock} disabled={saving || editLocked} variant="outline"
                className="w-full h-14 rounded-xl border-[#E1E6E4] text-[#1D242B] text-base font-semibold disabled:opacity-40" data-testid="unlock-draw-btn">
                <LockOpen className="w-5 h-5 mr-2" weight="bold" /> Unlock Draw
              </Button>
            )
          )}

          {/* Reset Draw — organiser only, draw locked */}
          {isOrganiser && round?.teams_locked && (
            <div className="space-y-1.5 pt-1">
              <Button
                onClick={() => setResetModalOpen(true)}
                disabled={hasScores}
                className={`w-full h-10 rounded-xl text-sm font-semibold gap-2 ${
                  hasScores
                    ? 'bg-[#F4F6F5] text-[#B8C7C0] cursor-not-allowed border border-[#E1E6E4]'
                    : 'bg-white text-[#D32F2F] border border-[#E1E6E4] hover:bg-red-50'
                }`}
                variant="outline"
                data-testid="reset-draw-btn">
                <ArrowCounterClockwise className="w-4 h-4" weight="bold" />
                Reset draw
              </Button>
              {hasScores && (
                <p className="text-xs text-[#D32F2F] text-center" data-testid="reset-draw-disabled-msg">
                  Draw can't be reset once scoring has started
                </p>
              )}
            </div>
          )}
        </div>
        )}

        {/* Reset Draw Confirmation Modal */}
        {resetModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6" data-testid="reset-draw-modal">
            <div className="bg-white rounded-2xl p-5 max-w-sm w-full space-y-4 shadow-xl">
              <h3 className="text-base font-bold text-[#1D242B]">Reset the draw for {round?.label || 'this round'}?</h3>
              <p className="text-sm text-[#455561]">
                Groups, teams and tee times for this round will be cleared so you can redo the draw. Other rounds are not affected.
              </p>
              <div className="flex gap-2 pt-1">
                <Button
                  onClick={() => setResetModalOpen(false)}
                  disabled={resetting}
                  className="flex-1 h-10 rounded-xl text-sm font-semibold bg-[#F4F6F5] text-[#455561] hover:bg-[#E1E6E4]"
                  variant="ghost"
                  data-testid="reset-draw-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={handleResetDraw}
                  disabled={resetting}
                  className="flex-1 h-10 rounded-xl text-sm font-semibold bg-[#D32F2F] text-white hover:bg-[#B71C1C]"
                  data-testid="reset-draw-confirm">
                  {resetting ? 'Resetting...' : 'Reset draw'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
